import { useCallback, useState, useEffect } from 'react'

import clsx from 'clsx'
import { useFormContext } from 'react-hook-form'
import ImageAddLineIcon from 'remixicon-react/ImageAddLineIcon'

import { Image } from '@tribeplatform/gql-client/types'
import { Form } from '@tribeplatform/react-sdk/components'
import {
  useNetwork,
  useUpdateNetwork,
  useUploadFile,
} from '@tribeplatform/react-sdk/hooks'
import { Button } from '@tribeplatform/react-ui-kit/Button'
import { Card } from '@tribeplatform/react-ui-kit/Card'
import { FormControl } from '@tribeplatform/react-ui-kit/FormControl'
import { SpinnerIcon } from '@tribeplatform/react-ui-kit/icons'
import { Link } from '@tribeplatform/react-ui-kit/Link'
import { toast } from '@tribeplatform/react-ui-kit/Toast'

import { linkToFile } from '../../../lib/upload'
import { PopoverImagePicker } from '../../Picker/ImagePicker'
import { TwitterSEOCard } from '../../SeoCards/Twitter'

const TitleHelperText = () => {
  const { watch } = useFormContext()
  const title = watch('title')

  return (
    <div className="flex justify-between text-basicSurface-300">
      <span>Recommended length: 60 characters</span>
      <span
        className={clsx(
          'text-bold',
          // eslint-disable-next-line no-nested-ternary
          title
            ? title.length > 60
              ? 'text-warning-800'
              : 'text-success-800'
            : '',
        )}
      >
        Length: {title?.length || 0}
      </span>
    </div>
  )
}

const DescriptionHelperText = () => {
  const { watch } = useFormContext()
  const description = watch('description')

  return (
    <div className="flex justify-between text-basicSurface-300">
      <span>Recommended length: 155 - 160 characters</span>
      <span
        className={clsx(
          'text-bold',
          // eslint-disable-next-line no-nested-ternary
          description
            ? description.length > 160 || description.length < 155
              ? 'text-warning-800'
              : 'text-success-800'
            : '',
        )}
      >
        Length: {description?.length || 0}
      </span>
    </div>
  )
}

const SeoImage = ({ defaultUrl }) => {
  const { setValue } = useFormContext()
  const { loadingFile, uploadFile } = useUploadFile()
  const [thumbnailUrl, setThumbnailUrl] = useState<string>(defaultUrl)

  useEffect(() => {
    if (loadingFile) setThumbnailUrl(loadingFile?.url)
  }, [loadingFile])

  const onUploadFile = useCallback(
    async (file: File) => {
      uploadFile(file, async uploadedImage => {
        if (uploadedImage.id) {
          setValue('thumbnailId', uploadedImage.id)
        }
      })
    },
    [uploadFile, setValue],
  )

  return (
    <div
      className="text-center mx-auto relative rounded-md bg-cover bg-no-repeat bg-center border-t border-b sm:border h-[180px] sm:h-[261px]"
      style={{
        width: '500px',
        borderColor: '#dadde1',
        backgroundImage: thumbnailUrl ? `url(${thumbnailUrl})` : '',
      }}
    >
      <div className="absolute top-0 bottom-0 left-0 right-0 flex justify-center items-center">
        <PopoverImagePicker
          tabsProps={{
            file: {
              helper: (
                <>
                  <p>Recommended size is 1200 x 630 pixels.</p>
                  <p>The file size limit is 10MB.</p>
                </>
              ),
            },
          }}
          onRemove={
            thumbnailUrl
              ? () => {
                  setThumbnailUrl(null)
                  setValue('thumbnailId', null)
                }
              : undefined
          }
          onLinkChange={async link => {
            const file = await linkToFile(link)
            if (file) onUploadFile(file)
          }}
          onImageChange={onUploadFile}
        >
          <div
            className={clsx(
              thumbnailUrl && 'bg-black opacity-70 rounded-md px-2 py-1',
            )}
          >
            {loadingFile ? (
              <SpinnerIcon className="w-9 h-9 animate-spin" />
            ) : (
              <>
                <div className="mb-3 text-basicSurface-300 flex justify-center">
                  <ImageAddLineIcon className="w-9 h-9" />
                </div>
                <Link>Choose an image</Link>
                <div className="text-basicSurface-300">
                  PNG, JPG, GIF up to 10MB
                </div>
              </>
            )}
          </div>
        </PopoverImagePicker>
      </div>
    </div>
  )
}

export const SeoSettings = () => {
  const { data: network } = useNetwork()
  const { mutateAsync: updateNetwork, isLoading } = useUpdateNetwork()
  const customSeoDetail = network?.customSeoDetail

  const updateSeoDetail = async ({
    title,
    description,
    noIndex,
    thumbnailId,
  }: {
    title: string
    description: string
    noIndex: boolean
    thumbnailId: string
  }) => {
    try {
      await updateNetwork({
        input: {
          customSeoDetail: {
            thumbnailId,
            title,
            description,
            noIndex,
          },
        },
      })
      toast({
        title: 'SEO details updated',
        status: 'success',
      })
    } catch (e) {
      toast({
        title: 'Unable to update SEO details',
        status: 'error',
      })
    }
  }

  return (
    <div className="max-w-3xl space-y-5">
      <Card>
        <Card.Header
          title="Search engine optimization"
          description="Search engine optimization (SEO) allows you to improve your ranking in
          search results. Use these features to make it easier for users to find
          your page when they search for it."
        />
        <Card.Content>
          <Form
            defaultValues={customSeoDetail}
            onSubmit={data => {
              const { title, description, noIndex, thumbnailId } = data
              updateSeoDetail({
                title,
                description,
                noIndex,
                thumbnailId,
              })
            }}
          >
            <div className="col-span-12 md:col-span-8 flex flex-col space-y-5">
              <Form.Input
                name="title"
                label="Meta title"
                helperText={<TitleHelperText />}
                placeholder="If empty, we will autogenerate this based on post title"
              />
              <Form.Textarea
                name="description"
                label="Meta description"
                placeholder="If empty, we will autogenerate this based on post content"
                helperText={<DescriptionHelperText />}
              />

              <div>
                <FormControl.Label>Social Image sharing</FormControl.Label>
                <FormControl.HelperText className="mt-2">
                  Social networks typically show your social sharing image
                  together with your meta title and description. If you don’t
                  add a social sharing image, we’ll use your community logo
                  instead.{' '}
                  {/* <Link variant="primary" href="#" target="_blank">
                    Learn More
                  </Link> */}
                </FormControl.HelperText>
              </div>
              <SeoImage
                defaultUrl={(customSeoDetail?.thumbnail as Image)?.url}
              />
              <Form.Toggle
                name="noIndex"
                label="Hide from search results"
                helperText="None of your community pages will be indexed by search engines."
              />

              <Form.Actions>
                <Button type="submit" loading={isLoading}>
                  Update
                </Button>
              </Form.Actions>
            </div>
          </Form>
        </Card.Content>
      </Card>
      <Card>
        <Card.Header
          title="Preview"
          description="Here is how your community link will look like when shared on social media."
        />
        <Card.Content>
          <TwitterSEOCard
            type="summary"
            title={customSeoDetail?.title || network?.name}
            domain={network?.domain}
            description={
              customSeoDetail?.description ||
              `Join ${network?.name} to start sharing and connecting with like-minded people.`
            }
            thumbnailUrl={
              (customSeoDetail?.thumbnail as Image)?.url ||
              (network?.logo as Image)?.url ||
              (network?.favicon as Image)?.url
            }
          />
        </Card.Content>
      </Card>
    </div>
  )
}
